import React, { useEffect } from 'react';
import { Divider, Form } from 'antd';

import { StyledSelect } from '@/lib/theme/components/Select';
import { Metadata } from '@/types';
import { StyledButton } from '@/lib/theme/components/Button';

import PlusIcon from '../Icons/PlusIcon';

type CommonDropDownFieldProps<T> = {
  data: T[] | undefined;
  metadata: Metadata | undefined;
  placeholder: string;
  disabled?: boolean;
  onChange?: (value: string | number) => void;
  isLoading: boolean;
  optionsListPage: number;
  setOptionsListPage: (page: number) => void;
  optionsHasNextPage: boolean;
  labelKey: string;
  additionalLabelKey?: string;
  setOptionsHasNextPage: (hasNextPage: boolean) => void;
  setOptionsSearchQuery: (query: string) => void;
  formItemName?: string;
  mode?: 'multiple' | 'tags' | undefined;
  canAddItem?: boolean;
  addItemBtnTxt?: string;
  onAddItemClick?: () => void;
};

type RecordType = { [key: string]: string | number; id: string | number };

function CommonDropDownField<T>({
  data,
  metadata,
  placeholder,
  onChange,
  isLoading,
  optionsListPage,
  setOptionsListPage,
  optionsHasNextPage,
  labelKey,
  additionalLabelKey,
  setOptionsHasNextPage,
  setOptionsSearchQuery,
  formItemName,
  disabled,
  mode,
  canAddItem,
  addItemBtnTxt,
  onAddItemClick,
}: CommonDropDownFieldProps<T>) {
  const [options, setOptions] = React.useState<{ label: string; value: number }[]>([]);

  const onOptionsScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.currentTarget;
    if (optionsHasNextPage && !isLoading && target.scrollTop + target.offsetHeight === target.scrollHeight) {
      setOptionsListPage(optionsListPage + 1);
      target.scrollTo(0, target.scrollHeight);
    }
  };

  const onOptionsSearch = (value: string) => {
    setOptionsSearchQuery(value);
    setOptionsListPage(1);
    setOptions([]);
  };

  useEffect(() => {
    if (!metadata) return;

    const totalRecords = metadata.total ?? 0;
    const pageSize = metadata.size ?? 10;
    const hasNextPage = Math.ceil(totalRecords / pageSize) > optionsListPage;
    setOptionsHasNextPage(hasNextPage);

    setOptions((prev) => {
      const _options = [...prev];
      const records: RecordType[] = (data as RecordType[]) || [];
      const newRecords = records.map((record: RecordType) => {
        return {
          label: additionalLabelKey
            ? String(record[labelKey]) + ` (${String(record[additionalLabelKey])})`
            : String(record[labelKey]),
          value: Number(record.id),
        };
      });

      newRecords.forEach((rec: { label: string; value: number }) => {
        if (!_options.some((item) => item.value === rec.value)) _options.push(rec);
      });
      return _options;
    });
  }, [data, optionsListPage, labelKey, additionalLabelKey, setOptionsHasNextPage, metadata]);

  const optionsFilterOption: (input: string, option?: { label: string; value: number }) => boolean = (
    input,
    option,
  ) => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  return (
    <Form.Item name={formItemName}>
      <StyledSelect
        showSearch
        disabled={disabled}
        loading={isLoading}
        onPopupScroll={onOptionsScroll}
        options={options}
        onSearch={onOptionsSearch}
        filterOption={optionsFilterOption}
        placeholder={placeholder}
        onChange={onChange}
        mode={mode}
        dropdownRender={(menu) => (
          <>
            {menu}
            {canAddItem && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <StyledButton
                  onClick={onAddItemClick}
                  type="primary"
                  ghost
                  size="small"
                  icon={<PlusIcon />}
                  style={{
                    marginInlineStart: 'auto',
                  }}
                >
                  {addItemBtnTxt}
                </StyledButton>
              </>
            )}
          </>
        )}
      />
    </Form.Item>
  );
}

export default CommonDropDownField;
